/**
 * Extract Comercial Gestão das Marcações notes from marcação form_data for CCC Recuperação tabs.
 */
import { RecuperacaoMarcacao } from '../api/cccApi';
import type { LeadDetailGroup, LeadDetailRow } from './recuperacaoMarcacaoDisplay';

const NOT_OK_CALLCENTER_LABELS: Record<string, string> = {
  data: 'Data',
  horario: 'Horário',
  contacto: 'Contacto',
  linkGoogleMaps: 'Link Google Maps',
  informacoesCallCenter: 'Informações CallCenter',
  outro: 'Outro',
};

const NOT_OK_CLIENTE_LABELS: Record<string, string> = {
  data: 'Data',
  horario: 'Horário',
  contacto: 'Contacto',
  linkGoogleMaps: 'Link Google Maps',
  informacoesCliente: 'Informações Cliente',
  outro: 'Outro',
};

const GESTAO_SUBSTAGE_TITLES: Record<string, string> = {
  'agendamentos-remarcacao': 'Remarcação',
  'reuniao-realizada-reuniao': 'Reunião Realizada',
  'feedback-pendencia-concluido-sem-proposta': 'Concluído Sem Proposta',
  'feedback-pendencia-concluido-com-proposta': 'Concluído Com Proposta',
  'feedback-pendencia-not-ok-cliente': 'Not Ok Cliente',
  'feedback-pendencia-not-ok-callcenter': 'Not Ok CallCenter',
  'feedback-pendencia-pendente': 'Pendente',
  'feedback-pendencia-solicitar-proposta': 'Solicitar Proposta',
  'resultado-fecho-ok': 'Fecho Ok',
  'resultado-fecho-not-ok': 'Fecho Not Ok',
  'resultado-aguardar': 'Aguardar',
  'resultado-cancelado': 'Cancelado',
};

const TEXT_FEEDBACK_SUBSTAGES = new Set([
  'reuniao-realizada-reuniao',
  'feedback-pendencia-concluido-sem-proposta',
  'feedback-pendencia-concluido-com-proposta',
  'feedback-pendencia-not-ok-cliente',
  'feedback-pendencia-pendente',
  'feedback-pendencia-solicitar-proposta',
  'resultado-fecho-ok',
  'resultado-fecho-not-ok',
  'resultado-aguardar',
  'resultado-cancelado',
]);

const getFeedbackText = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (value && typeof value === 'object' && 'text' in value) {
    const t = (value as { text?: unknown }).text;
    return typeof t === 'string' ? t.trim() : '';
  }
  return '';
};

const structuredFeedbackRows = (
  feedback: Record<string, unknown>,
  labels: Record<string, string>
): LeadDetailRow[] => {
  const rows: LeadDetailRow[] = [];
  Object.entries(feedback).forEach(([key, value]) => {
    const text = getFeedbackText(value);
    if (!text) return;
    rows.push({
      key,
      label: labels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()),
      value: text,
    });
  });
  return rows;
};

const hasStructuredContent = (obj: Record<string, unknown>): boolean =>
  Object.values(obj).some((v) => Boolean(getFeedbackText(v)));

const getFeedbackFromFormData = (fd: Record<string, unknown>): unknown => fd.feedback ?? null;

const getComercialGestaoSubstage = (m: RecuperacaoMarcacao): string =>
  (m.comercial_gestao_substage || '').trim();

function buildRemarcacaoGroup(fd: Record<string, unknown>): LeadDetailGroup | null {
  const reason = fd.rescheduleReason;
  const text = typeof reason === 'string' ? reason.trim() : '';
  if (!text) return null;
  return {
    id: 'gestao-remarcacao',
    title: GESTAO_SUBSTAGE_TITLES['agendamentos-remarcacao'],
    rows: [{ key: 'motivo', label: 'Motivo da remarcação', value: text }],
  };
}

function buildNotOkCallCenterGroup(fd: Record<string, unknown>): LeadDetailGroup | null {
  const feedback = getFeedbackFromFormData(fd);
  if (!feedback || typeof feedback !== 'object' || Array.isArray(feedback)) return null;
  const raw = feedback as Record<string, unknown>;
  const cc = raw.callCenterFeedback;
  if (!cc || typeof cc !== 'object' || Array.isArray(cc)) return null;
  const rows = structuredFeedbackRows(cc as Record<string, unknown>, NOT_OK_CALLCENTER_LABELS);
  if (!rows.length) return null;
  return {
    id: 'gestao-not-ok-callcenter',
    title: GESTAO_SUBSTAGE_TITLES['feedback-pendencia-not-ok-callcenter'],
    rows,
  };
}

function buildNotOkClienteGroup(fd: Record<string, unknown>): LeadDetailGroup | null {
  const feedback = getFeedbackFromFormData(fd);
  if (!feedback || typeof feedback !== 'object' || Array.isArray(feedback)) return null;
  const raw = feedback as Record<string, unknown>;
  if (raw.callCenterFeedback) return null;

  let cliente: Record<string, unknown> | null = null;
  if (raw.clienteFeedback && typeof raw.clienteFeedback === 'object' && !Array.isArray(raw.clienteFeedback)) {
    cliente = raw.clienteFeedback as Record<string, unknown>;
  } else {
    const clienteKeys = ['data', 'horario', 'contacto', 'linkGoogleMaps', 'informacoesCliente', 'outro'];
    if (clienteKeys.some((k) => k in raw)) {
      cliente = raw;
    }
  }
  if (!cliente || !hasStructuredContent(cliente)) return null;
  const rows = structuredFeedbackRows(cliente, NOT_OK_CLIENTE_LABELS);
  if (!rows.length) return null;
  return {
    id: 'gestao-not-ok-cliente',
    title: GESTAO_SUBSTAGE_TITLES['feedback-pendencia-not-ok-cliente'],
    rows,
  };
}

function buildTextFeedbackGroup(
  m: RecuperacaoMarcacao,
  fd: Record<string, unknown>
): LeadDetailGroup | null {
  const feedback = getFeedbackFromFormData(fd);
  if (typeof feedback !== 'string' || !feedback.trim()) return null;

  const substage = getComercialGestaoSubstage(m);
  const title =
    (substage && GESTAO_SUBSTAGE_TITLES[substage]) ||
    (substage && TEXT_FEEDBACK_SUBSTAGES.has(substage) ? substage : null) ||
    'Feedback Comercial';

  return {
    id: `gestao-text-feedback-${substage || 'generic'}`,
    title,
    rows: [{ key: 'nota', label: 'Nota', value: feedback.trim() }],
  };
}

function buildInformacoesComerciaisGroup(fd: Record<string, unknown>): LeadDetailGroup | null {
  const ic = fd.informacoesComerciais;
  if (!ic || typeof ic !== 'object' || Array.isArray(ic)) return null;
  const nota = (ic as Record<string, unknown>).nota;
  const text = nota != null ? String(nota).trim() : '';
  if (!text) return null;
  return {
    id: 'gestao-informacoes-comerciais',
    title: 'Informações Comerciais',
    rows: [{ key: 'nota', label: 'Nota', value: text }],
  };
}

/** Sections for Comercial tab — only includes substages that have saved note content. */
export function buildComercialGestaoNoteGroups(m: RecuperacaoMarcacao): LeadDetailGroup[] {
  const fd = (m.form_data || {}) as Record<string, unknown>;
  const groups: LeadDetailGroup[] = [];

  const remarcacao = buildRemarcacaoGroup(fd);
  if (remarcacao) groups.push(remarcacao);

  const notOkCallCenter = buildNotOkCallCenterGroup(fd);
  const notOkCliente = buildNotOkClienteGroup(fd);
  if (notOkCallCenter) groups.push(notOkCallCenter);
  if (notOkCliente) groups.push(notOkCliente);

  const textFeedback = buildTextFeedbackGroup(m, fd);
  if (textFeedback) {
    const skipText =
      (notOkCallCenter && textFeedback.id.includes('not-ok-callcenter')) ||
      (notOkCliente && textFeedback.id.includes('not-ok-cliente'));
    if (!skipText) groups.push(textFeedback);
  }

  const infoComercial = buildInformacoesComerciaisGroup(fd);
  if (infoComercial) groups.push(infoComercial);

  return groups;
}
